import React from "react";
import { Link } from "react-router-dom";
import { Check, Minus, Sparkles } from "lucide-react";
import { PLATFORM_COMPARE, getTierDisplayPricing } from "../../data/pricingContent";
import { usePricingPlans } from "../../hooks/usePricingPlans";

export const CompareMark = ({ ok }) =>
    ok === "true" ? (
        <Check className="w-4 h-4 text-electric mx-auto" strokeWidth={2.5} />
    ) : ok === "false" ? (
        <Minus className="w-4 h-4 text-zinc-700 mx-auto" />
    ) : (
        <span className="text-zinc-300 text-sm">{ok}</span>
    );

export const PricingCompareTable = ({
    className = "",
    testId = "pricing-compare-table",
    yearly = true,
    onTierCta,
    tiers: tiersProp,
    formatPrice: formatPriceProp,
}) => {
    const pricing = usePricingPlans();
    const tiers = tiersProp ?? pricing.tiers;
    const formatPrice = formatPriceProp ?? pricing.formatPrice;

    return (
        <div
            className={`rounded-2xl border border-white/[0.08] overflow-hidden bg-obsidian-200/60 backdrop-blur ${className}`}
            data-testid={testId}
        >
            <div className="overflow-x-auto">
                <table className="w-full text-left text-sm min-w-[640px]">
                    <thead className="bg-obsidian-100">
                        <tr>
                            <th className="py-5 px-5 text-zinc-500 font-medium text-xs uppercase tracking-wider w-[28%] align-bottom">
                                Plans
                            </th>
                            {tiers.map((t) => {
                                const isCustom = t.monthly === null;
                                const display = getTierDisplayPricing(t, yearly);

                                return (
                                    <th
                                        key={t.id}
                                        className={`py-5 px-4 text-center align-bottom w-[24%] ${
                                            t.primary
                                                ? "bg-electric/5 border-x border-electric/20"
                                                : ""
                                        }`}
                                    >
                                        <div className="relative flex flex-col items-center gap-2">
                                            {t.primary && (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-glow text-black text-[9px] font-bold uppercase tracking-[0.14em]">
                                                    <Sparkles className="w-2.5 h-2.5" />
                                                    Popular
                                                </span>
                                            )}
                                            <div className="font-display text-base font-semibold text-white">
                                                {t.name}
                                            </div>
                                            <div className="font-code text-[10px] uppercase tracking-[0.16em] text-electric">
                                                {t.licenses}
                                            </div>
                                            <div className="mt-1">
                                                {isCustom ? (
                                                    <div className="font-display text-2xl font-semibold text-white">
                                                        Custom
                                                    </div>
                                                ) : display?.showOffer ? (
                                                    <>
                                                        <div className="flex flex-wrap items-baseline justify-center gap-x-2 gap-y-0.5">
                                                            <span className="font-display text-lg font-medium text-zinc-500 line-through tabular-nums">
                                                                {formatPrice(display.listMonthly)}
                                                            </span>
                                                            <span className="font-display text-2xl font-semibold text-electric tabular-nums">
                                                                {formatPrice(display.offerMonthly)}
                                                            </span>
                                                        </div>
                                                        <div className="text-[11px] text-zinc-500">
                                                            / license / mo
                                                        </div>
                                                        <div className="inline-flex items-center gap-1.5 mt-1">
                                                            <span className="font-code text-[9px] uppercase tracking-[0.12em] text-amber-glow/90 bg-amber-glow/10 px-1.5 py-0.5 rounded">
                                                                −{display.savingsPercent}%
                                                            </span>
                                                            <span className="font-code text-[9px] text-zinc-600">
                                                                <span className="line-through text-zinc-700">
                                                                    {formatPrice(display.listYearly)}
                                                                </span>{" "}
                                                                {formatPrice(display.offerYearly)}/yr
                                                            </span>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <>
                                                        <div className="font-display text-2xl font-semibold text-white tabular-nums">
                                                            {formatPrice(display?.offerMonthly)}
                                                        </div>
                                                        <div className="text-[11px] text-zinc-500">
                                                            / license / mo
                                                        </div>
                                                        {yearly && (
                                                            <div className="font-code text-[9px] text-zinc-600 mt-0.5">
                                                                {formatPrice(t.yearly)}/yr per
                                                                license
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                            {onTierCta ? (
                                                <button
                                                    type="button"
                                                    onClick={() => onTierCta(t)}
                                                    className={`mt-2 w-full max-w-[140px] text-xs font-semibold py-2.5 rounded-full transition ${
                                                        t.primary
                                                            ? "bg-gradient-to-b from-electric to-electric-deep btn-glow text-white hover:brightness-110"
                                                            : "bg-white/5 border border-white/10 text-white hover:bg-white/10"
                                                    }`}
                                                    data-testid={`tier-${t.id}-cta`}
                                                >
                                                    {t.cta}
                                                </button>
                                            ) : (
                                                <Link
                                                    to={
                                                        t.isEnterprise || t.monthly === null
                                                            ? "/contact-sales"
                                                            : "/pricing"
                                                    }
                                                    className={`mt-2 w-full max-w-[140px] text-xs font-semibold py-2.5 rounded-full transition text-center ${
                                                        t.primary
                                                            ? "bg-gradient-to-b from-electric to-electric-deep btn-glow text-white hover:brightness-110"
                                                            : "bg-white/5 border border-white/10 text-white hover:bg-white/10"
                                                    }`}
                                                    data-testid={`tier-${t.id}-cta`}
                                                >
                                                    {t.cta}
                                                </Link>
                                            )}
                                        </div>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {PLATFORM_COMPARE.map((g) => (
                            <React.Fragment key={g.title}>
                                {g.rows.map((r) => (
                                    <tr
                                        key={`${g.title}-${r.f}`}
                                        className="border-t border-white/[0.04] hover:bg-white/[0.02] transition-colors"
                                    >
                                        <td className="py-3 px-5 text-zinc-300 text-sm">
                                            {r.f}
                                        </td>
                                        {r.v.slice(0, tiers.length).map((val, i) => (
                                            <td
                                                key={i}
                                                className={`py-3 px-5 text-center ${
                                                    tiers[i]?.primary
                                                        ? "bg-electric/[0.03] border-x border-electric/10"
                                                        : ""
                                                }`}
                                            >
                                                <CompareMark ok={val} />
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
